/**
 * Los dos app roles que expone el Admin API (design.md D2). Deben coincidir
 * exactamente con `nameof(AdminRole.Operador)` server-side
 * (`AdminAuthMiddleware.cs`).
 */
export type AdminRole = "Operador" | "Consulta";

const KNOWN_ROLES: readonly AdminRole[] = ["Operador", "Consulta"];

/**
 * Lee el claim `roles` de un payload de token ya decodificado. SIEMPRE debe
 * ser el del ACCESS TOKEN del Admin API (ver `useAccountRoles`), nunca el
 * ID token: appRoles y sus asignaciones viven en la app registration
 * "Panel Admin API", no en "Panel Admin SPA" -- el ID token (emitido con
 * `aud` = client id de la SPA, que no tiene appRoles propios) siempre trae
 * `roles` vacio sin importar el rol real del usuario.
 */
export function extractRoles(claims: Record<string, unknown> | undefined): AdminRole[] {
  if (!claims) {
    return [];
  }

  const raw = claims["roles"];
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.filter((value): value is AdminRole => KNOWN_ROLES.includes(value as AdminRole));
}

/**
 * Pure role-satisfaction check mirroring `AdminAuthMiddleware.EvaluateAsync`
 * server-side exactly: una exigencia de Consulta se satisface con
 * CUALQUIER rol autenticado (Operador es superset de Consulta); una
 * exigencia de Operador necesita el rol especifico. Esto es SOLO UX -- el
 * servidor valida el rol de forma independiente (`admin-panel-ui` spec,
 * "Restricciones de rol Consulta": el ocultamiento del cliente NUNCA debe
 * ser el unico control).
 */
export function hasRequiredRole(roles: readonly AdminRole[], required: AdminRole): boolean {
  if (roles.length === 0) {
    return false;
  }

  if (required === "Consulta") {
    return roles.includes("Operador") || roles.includes("Consulta");
  }

  return roles.includes("Operador");
}

/**
 * The single role to show where only one fits (e.g. the mobile "Más" sheet's
 * user card) — Operador is the superset role (see `hasRequiredRole` above),
 * so it must always win over Consulta when an account holds both, instead of
 * whichever one happens to come first in the token's `roles` claim array
 * (Codex finding, panel-redesign-v3 PR #381: `roles[0]` picked an arbitrary
 * one). Returns `undefined` for an unresolved/empty role list.
 */
export function getEffectiveRole(roles: readonly AdminRole[]): AdminRole | undefined {
  if (roles.includes("Operador")) {
    return "Operador";
  }
  return roles.includes("Consulta") ? "Consulta" : undefined;
}
